"use client";

import Image from "next/image";

import { getCloudFrontUrl } from "@/lib/cloudfront";
import { cn } from "@/lib/utils";

export interface FacepileProfile {
  connectionProfileId: string;
  firstName: string | null;
  lastName: string | null;
  photoS3Key: string | null;
}

function facepileInitials(
  firstName: string | null,
  lastName: string | null,
): string {
  const first = firstName?.trim()?.[0] ?? "";
  const last = lastName?.trim()?.[0] ?? "";
  return (first + last).toUpperCase() || "?";
}

const SIZE = {
  sm: { dim: 24, text: "text-[9px]" },
  md: { dim: 28, text: "text-[10px]" },
} as const;

export function ConnectionFacepile({
  profiles,
  totalCount,
  max = 3,
  size = "sm",
  className,
}: {
  profiles: FacepileProfile[];
  totalCount: number;
  max?: number;
  size?: "sm" | "md";
  className?: string;
}) {
  const shown = profiles.slice(0, max);
  if (shown.length === 0) {
    return null;
  }
  const overflow = Math.max(0, totalCount - shown.length);
  const { dim, text } = SIZE[size];

  return (
    <div className={cn("flex items-center -space-x-1.5", className)}>
      {shown.map((profile) => {
        const photoUrl = getCloudFrontUrl(profile.photoS3Key);
        return (
          <div
            key={profile.connectionProfileId}
            className="ring-card bg-muted relative shrink-0 overflow-hidden rounded-full ring-2"
            style={{ width: dim, height: dim }}
          >
            {photoUrl ? (
              <Image
                src={photoUrl}
                alt=""
                fill
                sizes={`${dim}px`}
                className="object-cover"
              />
            ) : (
              <span
                className={cn(
                  "text-muted-foreground flex size-full items-center justify-center font-semibold",
                  text,
                )}
              >
                {facepileInitials(profile.firstName, profile.lastName)}
              </span>
            )}
          </div>
        );
      })}
      {overflow > 0 && (
        <span
          className={cn(
            "ring-card bg-foreground text-background relative flex shrink-0 items-center justify-center rounded-full font-semibold ring-2",
            text,
          )}
          style={{ width: dim, height: dim }}
          aria-label={`${overflow} more`}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
